/**
 * Manual scroll restoration for the hand-rolled pushState router.
 *
 * The browser's native scroll restoration animates (app.css sets
 * `scroll-behavior: smooth` on :root for in-page anchors) and, worse, fires
 * before a re-mounted page's live query has grown the document tall enough to
 * hold the old position — so back-nav visibly jumps to the top then scrolls
 * down. We take over: history.scrollRestoration = 'manual', a per-entry scroll
 * key minted into history.state, the current entry's Y recorded to
 * sessionStorage, and an rAF loop that waits for content height before restoring.
 *
 * The pure/decision helpers (key minting, storage save/read, restore-target
 * selection) live here and are unit-tested; the animation-frame loop is the thin
 * imperative wrapper that uses them.
 */

const STORAGE_PREFIX = 'gravity:scroll:';
/** Give a cache-cold page this long to grow tall enough before we give up. */
export const RESTORE_DEADLINE_MS = 2000;

let keyCounter = 0;

/** Mint a unique-per-entry scroll key (counter + time, base36). App-local id. */
export function mintScrollKey(): string {
  keyCounter += 1;
  return `${keyCounter.toString(36)}-${Date.now().toString(36)}`;
}

/** Pull a scroll key out of a history.state object, if present and a string. */
export function readScrollKey(state: unknown): string | null {
  if (state && typeof state === 'object' && 'scrollKey' in state) {
    const value = (state as { scrollKey?: unknown }).scrollKey;
    if (typeof value === 'string' && value) return value;
  }
  return null;
}

/** Persist a scroll Y for a key. sessionStorage survives reload; Safari private
 *  mode throws on access, so every call is guarded. */
export function saveScrollPosition(key: string, y: number): void {
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(Math.round(y)));
  } catch {
    // Storage unavailable (private mode, quota) — restoration silently degrades.
  }
}

/** Read a saved scroll Y for a key, or null if none / storage unavailable. */
export function readScrollPosition(key: string | null): number | null {
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Largest scrollable Y for the current document. */
export function maxScrollable(): number {
  return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
}

/**
 * Given a target Y and the current max scrollable height, decide the restore step:
 * - 'restore' once the content is tall enough to hold targetY (or on deadline
 *   expiry, clamped to what's scrollable);
 * - 'wait' while it's still too short and the deadline hasn't passed.
 * Pure so the decision is unit-testable without rAF.
 */
export function decideRestoreStep(
  targetY: number,
  maxY: number,
  expired: boolean
): { action: 'restore'; y: number } | { action: 'wait' } {
  if (maxY >= targetY) return { action: 'restore', y: targetY };
  if (expired) return { action: 'restore', y: Math.min(targetY, maxY) };
  return { action: 'wait' };
}
